
import { Component, Vue, Watch, Prop } from "vue-property-decorator"
import { namespace, Action, Getter } from "vuex-class"
import { CaseColumn, caseColumnToField } from "@/types/caseColumns"
import FieldDropDown from "@/components/Config/Rules/FieldDropDown.vue"
import vSelect from "vue-select"
import HeaderList from "./HeaderList.vue"
import RecipientList from "./RecipientList.vue"
import RichTextTemplate from "@/components/Controls/RichTextTemplate.vue"
import { IntegrationOutOption } from "@/types/integration"
import { cloneDeep } from "lodash"
import { TenantFeatures } from "@/types/tenantFeatures"
import Guid from "@/scripts/guid"
import { Format } from "@/types/format"
import { readOnlyFieldTypes, FieldDataTypes } from "@/types/fieldTypes"
import { StoreModules } from "@/store/StoreModuleTypes"
import NewValueSelector from "./NewValueSelector.vue"
import HelpIcon from "@/components/New/Page/PageComponents/HelpIcon.vue"

import {
	AllowFieldTypeRequest,
	getNonActionSettableFieldTypes,
	nonAddableFieldTypes,
	settingsJsonStringToValue
} from "@/utils/rulesUtils"

import {
	NewValueSelectorProps,
	NewValueSelectorPropsForExpression,
	NewValueSelectorPropsForField,
	NewValueSettingLabels,
	NewValueSettingOption,
	NewValueSettingOptions
} from "@/types/rule/newValueSelector"

import {
	actionSettingsForActionType,
	actionSetting,
	ActionSettingField,
	ActionSetting,
	ActionSettingIds,
	ActionType,
	CloseOptions,
	CloseOptionsLabels,
	DataType,
	NewValueSetting,
	notificationFunctions,
	RecipientType,
	RuleActionDto,
	TimeZone,
	Urgency,
	UrgencyLabels
} from "@/types"

const setup = namespace(StoreModules.Setup)

@Component({
	components: {
		"field-dropdown": FieldDropDown,
		"new-value-selector": NewValueSelector,
		"v-select": vSelect,
		"header-list": HeaderList,
		"recipient-list": RecipientList,
		"rich-text-template": RichTextTemplate,
		"help-icon": HelpIcon
	}
})
export default class RuleActionsSettings extends Vue {
	@Action public loadTeams!: () => void;

	@Getter("getIsFeatureEnabled") getIsFeatureEnabled!: (feature: TenantFeatures) => boolean
	@Getter("getTenantTimezones") timezones!: TimeZone[]

	@setup.State("teams") teams!: any[];

	@Prop()
	public fieldOptions!: CaseColumn[];

	@Prop({ default: null })
	private action!: RuleActionDto;

	@Prop({ default: () => [] })
	private integrationOuts!: IntegrationOutOption[]

	@Prop({ default: () => () => [] })
	private formatsGet!: () => Promise<Format[]>

	private fields!: CaseColumn[]
	private actionSettingsByType = actionSetting.toDictionary("type");
	private actionSettingsById = actionSetting.toDictionary("id");
	private actionSettingField = ActionSettingField;
	private actionType = ActionType;
	private formats: Format[] | null = null

	private allowedDataTypeForFieldSetting: string[] = [
		FieldDataTypes.Checkbox,
		FieldDataTypes.Date,
		FieldDataTypes.DateTime,
		FieldDataTypes.Dollar,
		FieldDataTypes.Number,
		FieldDataTypes.Text,
		FieldDataTypes.TextArea,
		FieldDataTypes.ExternalLink
	]

	private allowedDataTypeForExpressionSetting: string[] = [
		FieldDataTypes.Date,
		FieldDataTypes.DateTime,
		FieldDataTypes.Number,
		FieldDataTypes.Dollar
	]

	private urgencyLabels = UrgencyLabels
	private closeOptionsLabels = CloseOptionsLabels

	private get selectedIntegration (): IntegrationOutOption | null {
		if (!this.action || !this.action.settings || !this.action.settings.integration) {
			return null
		}

		const option = this.integrationOuts.find(o => o.integrationKey === this.action.settings.integration &&
			o.integrationOut === this.action.settings.integrationOut)
		return option || null
	}
	private set selectedIntegration (value: IntegrationOutOption | null) {
		if (!this.action) {
			return
		}

		Vue.set(this.action.settings, ActionSettingIds.Integration, value ? value.integrationKey : null)
		Vue.set(this.action.settings, "integrationOut", value ? value.integrationOut : null)
	}

	private get selectableFields (): CaseColumn[] {
		if (!this.fieldOptions) return []

		if (!this.action) {
			return []
		}

		switch (this.action.actionType) {
			case ActionType.SetValue:
				var allowUserRole: boolean = this.getIsFeatureEnabled(TenantFeatures.ActionsSetValueUserRole)

				const request: AllowFieldTypeRequest = {
					userRole: allowUserRole
				}
				const excludedTypes: string[] = getNonActionSettableFieldTypes(request)
				return this.fieldOptions.filter(f => !excludedTypes.includes(f.dataType))
			case ActionType.SetFieldRequired:
				return this.fieldOptions.filter(f => f.dynamicField && !readOnlyFieldTypes.includes(f.dataType))
			case ActionType.AddField:
				return this.fieldOptions.filter(f => f.dynamicField && !nonAddableFieldTypes.includes(f.dataType))
			default:
				throw new Error("Action Type Not implemented")
		}
	}

	private get templateFields (): { id: string, label: string }[] {
		if (this.action.actionType === ActionType.SendNotification) {
			return [{
				id: "recipientName", label: "Recipient Name"
			}]
		} else if (this.action.actionType === ActionType.SendEmail) {
			return [
				{ id: "recipientName", label: "Recipient Name" },
				{ id: "recordLink", label: "Record Link" }
			]
		}
		return []
	}

	/**
	 * Gets format list
	 * Fetches from prop if this has not already been populated
	 */
	private get formatOptions (): Format[] {
		if (!this.formats) {
			this.formatsGet().then(formats => this.formats = formats)
		}
		return this.formats || []
	}

	private get closeOptions (): {id: CloseOptions, title: string}[] {
		return CloseOptionsLabels.map(x => ({ id: x.key, title: x.label }))
	}

	private showFieldSelector (type: number): boolean {
		if (this.actionSettingField && type === this.actionSettingField.Field) {
			return true
		} else if (!this.actionSettingField) {
			return false
		}
		return type === (this.actionSettingField.FieldId || this.actionSettingField.FieldDataType)
	}

	private teamSelected (team: any): void {
		this.action.settings[ActionSettingIds.TeamId] = `${team.id}`
		this.action.settings[ActionSettingIds.TeamTitle] = `${team.title}`
	}

	private mounted (): void {
		// If necessary, convert old email recipient to new recipient list
		if (this.action.actionType === ActionType.SendEmail) {
			if (this.getIsFeatureEnabled(TenantFeatures.AdvancedEmailRule)) {
				// Convert recipient to item in recipient list
				if (this.action.settings[ActionSettingIds.Recipient] && !this.action.settings[ActionSettingIds.RecipientList]) {
					this.action.settings[ActionSettingIds.RecipientList] = [
						{
							id: Guid.newGuid(),
							type: RecipientType.EmailAddress,
							name: "",
							emailAddress: this.action.settings[ActionSettingIds.Recipient]
						}
					] as any
				}
			}
		}

		this.updateSetValueActionSettingsOnMount()

		// Initialize the component with the same function used when the action type changes
		this.onActionTypeChanged(this.action.actionType)
		this.loadTeams()
	}

	public get actionTypeSettings (): ActionSetting[] {
		return actionSettingsForActionType[this.action.actionType]
			.map(t => actionSetting.find(setting => t === setting.type)!)
			.filter(t => !!t && (!t.show || t.show(this.action.actionType, this.getIsFeatureEnabled)))
	}

	private get excludeInactiveUsers (): boolean {
		return this.isSetValueActionForUserRole()
	}

	private get hideTimeSpentForUserRole (): boolean {
		return this.isSetValueActionForUserRole()
	}

	private get allCaseFieldColumns (): CaseColumn[] {
		return this.fields
	}

	private get newValueSelectorProps (): NewValueSelectorProps | null {
		if (this.newValueSetting.key === NewValueSetting.Manual) {
			return {
				value: this.action.settings[ActionSettingIds.NewValue],
				dynamicValue: this.action.dynamic,
				allCaseFieldColumns: this.allCaseFieldColumns,
				excludeInactiveUsers: this.excludeInactiveUsers,
				hideTimeSpentForUserRole: this.hideTimeSpentForUserRole
			} as NewValueSelectorProps
		}

		return null
	}

	private get newValuePropsForField (): NewValueSelectorPropsForField | null {
		if (this.newValueSetting.key === NewValueSetting.Field) {
			var fieldData = this.selectableFields.find(f => f.key === this.action?.settings[ActionSettingIds.NewValue])

			if (!fieldData) {
				// Applies to Severity and Status
				fieldData = this.selectableFields.find(f => f.filterKey === this.action?.settings[ActionSettingIds.NewValue])
			}

			return {
				value: fieldData,
				selectableFields: this.selectableFields.filter(cf => cf.dataType === this.action?.fieldData?.dataType &&
				(cf.filterKey ? cf.filterKey !== this.action?.fieldData?.filterKey : cf.key !== this.action?.fieldData?.key))
			} as NewValueSelectorPropsForField
		}

		return null
	}

	private get newValuePropsForExpression (): NewValueSelectorPropsForExpression | null {
		if (this.newValueSetting.key === NewValueSetting.Expression) {
			let fieldOptionsForExpression = this.fields ?? []

			if (this.action?.fieldData && this.action?.fieldData?.dataType && this.action?.fieldData?.dataType === FieldDataTypes.Number) {
				fieldOptionsForExpression = fieldOptionsForExpression?.filter(f => f.dataType === this.action?.fieldData?.dataType &&
				(f.filterKey ? f.filterKey !== this.action?.fieldData?.filterKey : f.key !== this.action?.fieldData?.key))
			}

			return {
				value: this.action.settings[ActionSettingIds.NewValue],
				fieldOptions: fieldOptionsForExpression.map(f => caseColumnToField(f)),
				closeOnSelect: this.closeOnSelect
			} as NewValueSelectorPropsForExpression
		}

		return null
	}

	private get newValueSetting (): NewValueSettingOption {
		var key = NewValueSetting.Manual

		if (this.action?.settings[ActionSettingIds.Field] && this.action?.settings[ActionSettingIds.NewValueSetting]) {
			key = parseInt(this.action.settings[ActionSettingIds.NewValueSetting] as string)
		}

		return NewValueSettingOptions[key]
	}

	/**
	 * Map NewValueSetting enum to options for v-select
	 */
	private get settingOptions (): NewValueSettingOption[] {
		var options: NewValueSettingOption[] = [
			NewValueSettingOptions[NewValueSetting.Manual]
		]

		if (this.action?.fieldData) {
			const fieldDataType: string = this.action?.fieldData.dataType

			if (this.allowedDataTypeForFieldSetting.includes(fieldDataType)) {
				options.push(NewValueSettingOptions[NewValueSetting.Field])
			}

			if (this.allowedDataTypeForExpressionSetting.includes(fieldDataType)) {
				options.push(NewValueSettingOptions[NewValueSetting.Expression])
			}
		}

		return options
	}

	private get allowSetValueUsingFieldOrExpression () : boolean {
		return this.getIsFeatureEnabled(TenantFeatures.ActionsSetValueUsingFieldOrExpression)
	}

	private get closeOnSelect (): boolean {
		return !(this.getIsFeatureEnabled(TenantFeatures.MultiSelectKeepOpen))
	}

	private showSetting (setting: ActionSetting): boolean {
		if (setting.visible) {
			switch (setting.type) {
				case ActionSettingField.HttpHeaders:
					// http headers is handled different from other action types
					return false
				case ActionSettingField.NewValueSetting:
					// new value setting dropdown shown only for field data types,
					// supported for field or expression.
					if (this.action?.fieldData) {
						const fieldDataType: string = this.action?.fieldData.dataType!
						return this.allowedDataTypeForFieldSetting.includes(fieldDataType) ||
						this.allowedDataTypeForExpressionSetting.includes(fieldDataType)
					}
					return false
				default:
					break
			}

			return true
		}

		return false
	}

	@Watch("action.actionType")
	private onActionTypeChanged (value: number | null): void {
		if (!this.action.settings) {
			this.action.settings = {}
		}

		// Get the fields based on the action type
		const actionSettingFields = actionSettingsForActionType[this.action.actionType] || []

		// Delete values from saved action.settings where action type has changed
		for (var id in this.action.settings) {
			if (!actionSettingFields.includes(this.actionSettingsById[id].type)) {
				delete this.action.settings[id]
			}
		}

		// Create an dictionary to be able to capture the editable field values
		actionSettingFields.forEach(l => {
			const labelName = this.actionSettingsByType[l].id

			if (l === ActionSettingField.DynamicValue) {
				Vue.set(this.action, "dynamic", this.action.settings[labelName]
					? Boolean(this.action.settings[labelName])
					: false)
			}

			if (this.action.settings[labelName] === undefined) {
				Vue.set(this.action.settings, labelName, "")
			}
		})

		this.fields = cloneDeep(this.fieldOptions)
		if (this.action.actionType !== ActionType.AddField) {
			this.fields.removeWhere(f => f.dataType === FieldDataTypes.ListData)
		}

		if (this.action.settings[ActionSettingIds.Field]) {
			let fieldData = this.fields.find(f => f.key === this.action.settings[ActionSettingIds.Field])

			if (!fieldData) {
				// Applies to Severity and Status
				fieldData = this.fields.find(f => f.filterKey === this.action.settings[ActionSettingIds.Field])
			}
			Vue.set(this.action, "fieldData", fieldData)
		} else if (this.action.settings[ActionSettingIds.FieldId]) {
			Vue.set(this.action, "fieldData", this.fields.find(f => f.key === "fields." + this.action.settings[ActionSettingIds.FieldId]))
		} else {
			Vue.set(this.action, "fieldData", null)
		}
	}

	private dynamicValueChanged (isDynamic: boolean): void {
		Vue.set(this.action.settings, ActionSettingIds.NewValue, null)
		Vue.set(this.action.settings, ActionSettingIds.DynamicValue, isDynamic.toString())
		Vue.set(this.action, "dynamic", isDynamic)
	}

	private fieldUpdated (field: CaseColumn | null): void {
		var actionTypeSettingsList = this.actionTypeSettings

		if (field) {
			Vue.set(this.action.settings, ActionSettingIds.Field, field.filterKey ? field.filterKey : field.key)

			if (field.dynamicField) {
				Vue.set(this.action.settings, ActionSettingIds.FieldId, field.key.substring(7))
			}

			Vue.set(this.action.settings, ActionSettingIds.FieldDataType, field.fieldDataType!.toString())
			Vue.set(this.action.settings, ActionSettingIds.ValueFormat, field.dataType)
			Vue.set(this.action.settings, ActionSettingIds.NewValue, null)
		} else {
			this.onNullFieldResetActionSettings()
		}

		if (actionTypeSettingsList.find(setting => setting.type === ActionSettingField.NewValueSetting)) {
			Vue.set(this.action.settings, ActionSettingIds.NewValueSetting, NewValueSetting.Manual.toString())
		}
	}

	private settingUpdated (value: string, settingId: string): void {
		Vue.set(this.action.settings, settingId, value)
	}

	private urgencyText (urgency: Urgency): string {
		return notificationFunctions.urgencyToString(urgency)!
	}

	private newValueLabelUpdated (newLabel: string): void {
		Vue.set(this.action.settings, ActionSettingIds.NewValueLabel, newLabel)
	}

	private newValueUpdated (newValue: any): void {
		if (this.newValueSetting.key === NewValueSetting.Manual) {
			if (this.isSetValueActionForUserRole()) {
				// ActionType SetValue Value for UserRole, no need to convert to string
				Vue.set(this.action.settings, ActionSettingIds.NewValue, newValue)
			} else {
				Vue.set(this.action.settings, ActionSettingIds.NewValue, newValue?.toString() ?? null)
			}
		} else {
			Vue.set(this.action.settings, ActionSettingIds.NewValue, newValue)
			Vue.set(this.action.settings, ActionSettingIds.NewValueLabel, null)
		}
	}

	private newValueSettingUpdated (newValue: NewValueSetting | null): void {
		if (newValue !== null) {
			Vue.set(this.action.settings, ActionSettingIds.NewValueSetting, newValue.toString())
		} else {
			Vue.set(this.action.settings, ActionSettingIds.NewValueSetting, NewValueSetting.Manual.toString())
		}

		// reset the new value
		Vue.set(this.action.settings, ActionSettingIds.NewValue, null)
	}

	private isSetValueActionForUserRole (): boolean {
		return this.action?.actionType === ActionType.SetValue &&
		this.action?.settings && this.action?.settings[ActionSettingIds.FieldDataType] === DataType.userRole.toString()
	}

	private isSetValueActionForNewValueSetting (setting: NewValueSetting): boolean {
		return this.action?.actionType === ActionType.SetValue &&
		this.action?.settings && this.action?.settings[ActionSettingIds.NewValueSetting] === setting.toString()
	}

	private hasActionSetting (actionSettingKey: string): boolean {
		return (this.action?.settings && this.action?.settings[actionSettingKey] != null)
	}

	private updateSetValueActionSettingsOnMount () {
		if (this.action?.actionType === ActionType.SetValue) {
			// set New Value Setting to default is not present
			if (!this.hasActionSetting(ActionSettingIds.NewValueSetting)) {
				Vue.set(this.action.settings, ActionSettingIds.NewValueSetting, NewValueSetting.Manual.toString())
			}

			if (this.hasActionSetting(ActionSettingIds.NewValue)) {
				switch (this.action?.settings[ActionSettingIds.NewValueSetting]) {
					case NewValueSetting.Manual.toString():
						if (this.action?.settings[ActionSettingIds.FieldDataType] === DataType.userRole.toString()) {
							settingsJsonStringToValue(this.action, ActionSettingIds.NewValue)
						}

						break
					case NewValueSetting.Expression.toString():
						settingsJsonStringToValue(this.action, ActionSettingIds.NewValue)
						break
					default:
						break
				}
			}
		}
	}

	private allowSetting (option: NewValueSettingOption): boolean {
		if (this.allowSetValueUsingFieldOrExpression) {
			return true
		} else if (option.key !== NewValueSetting.Manual) {
			return false
		}

		return true
	}

	private onNullFieldResetActionSettings (): void {
		Vue.set(this.action.settings, ActionSettingIds.Field, null)
		Vue.set(this.action.settings, ActionSettingIds.FieldId, null)
		Vue.set(this.action.settings, ActionSettingIds.FieldDataType, null)
		Vue.set(this.action.settings, ActionSettingIds.ValueFormat, null)
		Vue.set(this.action.settings, ActionSettingIds.NewValue, null)
	}

	private ruleActionSettingEnabled (settingType: ActionSettingField) : boolean {
		switch (settingType) {
			case ActionSettingField.NewValue:
				if (this.isSetValueActionForUserRole()) {
					return this.getIsFeatureEnabled(TenantFeatures.ActionsSetValueUserRole)
				} else if (!this.isSetValueActionForNewValueSetting(NewValueSetting.Manual)) {
					return this.allowSetValueUsingFieldOrExpression
				}

				break
			case ActionSettingField.NewValueSetting:
				if (!this.isSetValueActionForNewValueSetting(NewValueSetting.Manual)) {
					return this.allowSetValueUsingFieldOrExpression
				}

				break
			default:
				break
		}

		return true
	}

	private getInfoToolTipText (settingType: ActionSettingField) : string | null {
		switch (settingType) {
			case ActionSettingField.NewValue:
				if (this.isSetValueActionForUserRole()) {
					return "Assigned user role field value is not enabled for edit"
				} else if (!this.isSetValueActionForNewValueSetting(NewValueSetting.Manual)) {
					return "Assigned field value is not enabled for edit"
				}

				break
			case ActionSettingField.NewValueSetting:
				let infoText: string = NewValueSettingLabels.Field

				if (this.isSetValueActionForNewValueSetting(NewValueSetting.Expression)) {
					infoText = NewValueSettingLabels.Expression
				}

				return `Set New value ${infoText} is not enabled for edit`
			default:
				break
		}

		return null
	}
}
